import React, { useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { BellRing, FileText, HardDrive, Users, UserMinus, Clock, Activity } from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Sector,
  Tooltip as RechartsTooltip,
} from 'recharts';
import {
  fetchHealthRecordUsage,
  type HealthRecordUsageByType,
} from '../../services/MasterAdmin/healthRecordUsageService';
import type { MetricInfoDefinition } from '../../components/MasterAdmin/ui/MetricInfoPopover';
import { healthRecordUsageMetricInfo } from '../../config/masterAdminMetricDefinitions';
import CampaignFormModal from '../../components/MasterAdmin/push-notifications/CampaignFormModal';
import SendConfirmModal from '../../components/MasterAdmin/push-notifications/SendConfirmModal';
import type { PushNotificationCampaign } from '../../models/MasterAdmin/pushNotificationCampaign';
import InsightsPageShell from '../../components/MasterAdmin/insights/InsightsPageShell';
import InsightsStatCard from '../../components/MasterAdmin/insights/InsightsStatCard';
import InsightsChartCard, { INSIGHTS_CHART_TOOLTIP } from '../../components/MasterAdmin/insights/InsightsChartCard';
import InsightsSectionHeader from '../../components/MasterAdmin/insights/InsightsSectionHeader';
import { opsPrimaryBtn, opsTableWrap, opsChartCard, PIE_PALETTE } from '../../components/MasterAdmin/operations/operationsUi';

const STAT_GRID_CLASS = 'grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3';

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** idx;
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[idx]}`;
}

const pageInfo: MetricInfoDefinition = {
  definition:
    'Health record storage usage across the platform: record counts, storage footprint, format distribution, and outreach to users without saved records.',
  formula: healthRecordUsageMetricInfo.totalRecords.formula,
  sources: healthRecordUsageMetricInfo.totalRecords.sources,
};

const ChartTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div style={INSIGHTS_CHART_TOOLTIP} className="p-3 text-left text-xs">
      <p className="mb-1.5 font-semibold text-gray-200">{String(data.name || '').replace(/_/g, ' ')}</p>
      <div className="flex justify-between gap-4">
        <span className="text-gray-400">Files</span>
        <span className="font-semibold text-white">{data.count}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-400">Storage</span>
        <span className="font-semibold text-blue-400">{formatBytes(data.value)}</span>
      </div>
    </div>
  );
};

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 6}
        outerRadius={innerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={0.3}
      />
    </g>
  );
};

const formatFriendlyLabel = (type: string): string => {
  const mapping: Record<string, string> = {
    USER_PROFILE_PHOTO: 'Profile Avatars',
    CHAT_MESSAGE_FILE: 'Consultation Chat Files',
    VIDEO_SDK_CHAT_FILE: 'Video Consultation Files',
    BLOOD_REQUEST_PRESCRIPTION: 'Blood Request Prescriptions',
    MEDICINE_PRESCRIPTION_UPLOAD: 'Medicine Order Prescriptions',
    LAB_TEST_PRESCRIPTION: 'Lab Test Bookings',
    LAB_TEST_REPORT: 'Lab Test Reports',
    FEEDBACK_ATTACHMENT: 'User Feedback Files',
    GRIEVANCE_ATTACHMENT: 'User Support Files',
    RETURN_REFUND_EVIDENCE: 'Return Refund Claims',
  };
  return mapping[type] || type.replace(/_/g, ' ');
};

type DonutBreakdownProps = {
  data: { name: string; value: number; count: number }[];
  total: number;
  activeIndex: number | null;
  setActiveIndex: (idx: number | null) => void;
  paletteOffset?: number;
  labelFormatter?: (name: string) => string;
};

const DonutChart: React.FC<DonutBreakdownProps> = ({
  data,
  total,
  activeIndex,
  setActiveIndex,
  paletteOffset = 0,
  labelFormatter = (name) => name.replace(/_/g, ' '),
}) => {
  if (data.length === 0) {
    return <div className="flex h-full items-center justify-center text-sm text-gray-500">No storage stats found.</div>;
  }
  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart onMouseLeave={() => setActiveIndex(null)}>
          <RechartsTooltip content={<ChartTooltip />} />
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={78}
            outerRadius={100}
            paddingAngle={3}
            dataKey="value"
            activeIndex={activeIndex !== null ? activeIndex : undefined}
            activeShape={renderActiveShape}
            onMouseEnter={(_, index) => setActiveIndex(index)}
          >
            {data.map((_entry, index) => (
              <Cell key={`cell-${index}`} fill={PIE_PALETTE[(index + paletteOffset) % PIE_PALETTE.length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute flex max-w-[170px] select-none flex-col items-center justify-center px-4 text-center">
        {activeIndex !== null && data[activeIndex] ? (
          <>
            <span className="w-full truncate text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {labelFormatter(data[activeIndex].name)}
            </span>
            <span className="mt-0.5 text-2xl font-bold text-gray-900 dark:text-white">
              {formatBytes(data[activeIndex].value)}
            </span>
            <span className="mt-0.5 text-[11px] font-semibold text-primary-600 dark:text-primary-400">
              {total > 0 ? `${((data[activeIndex].value / total) * 100).toFixed(1)}%` : '0.0%'}
            </span>
          </>
        ) : (
          <>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Total Volume
            </span>
            <span className="mt-0.5 text-2xl font-bold text-gray-900 dark:text-white">{formatBytes(total)}</span>
          </>
        )}
      </div>
    </div>
  );
};

type BreakdownListProps = {
  rows: HealthRecordUsageByType[];
  total: number;
  activeIndex: number | null;
  setActiveIndex: (idx: number | null) => void;
  paletteOffset?: number;
  labelFormatter?: (type: string) => string;
  emptyLabel: string;
};

const BreakdownList: React.FC<BreakdownListProps> = ({
  rows,
  total,
  activeIndex,
  setActiveIndex,
  paletteOffset = 0,
  labelFormatter = (t) => t,
  emptyLabel,
}) => {
  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-200 p-12 text-sm text-gray-400 dark:border-gray-700">
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="max-h-[320px] space-y-3 overflow-y-auto pr-1">
      {rows.map((row, idx) => {
        const share = total > 0 ? (Number(row.bytes || 0) / total) * 100 : 0;
        const color = PIE_PALETTE[(idx + paletteOffset) % PIE_PALETTE.length];
        const isActive = activeIndex === idx;
        return (
          <div
            key={row.type}
            onMouseEnter={() => setActiveIndex(idx)}
            onMouseLeave={() => setActiveIndex(null)}
            className={`cursor-pointer rounded-xl border p-3.5 transition-all duration-200 ${
              isActive
                ? 'border-gray-300 bg-gray-100/80 shadow-sm dark:border-gray-600 dark:bg-gray-800/60'
                : 'border-gray-100 bg-gray-50/50 dark:border-gray-800 dark:bg-gray-900/40'
            }`}
          >
            <div className="flex items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-800 dark:text-gray-200">
                  {labelFormatter(row.type || 'UNKNOWN')}
                </span>
              </div>
              <span className="whitespace-nowrap text-xs font-medium text-gray-500 dark:text-gray-400">
                <span className="font-mono text-gray-800 dark:text-gray-200">{row.count}</span> files ·{' '}
                <span className="font-mono text-gray-800 dark:text-gray-200">{formatBytes(Number(row.bytes || 0))}</span>{' '}
                ({share.toFixed(1)}%)
              </span>
            </div>
            <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-gray-200/70 dark:bg-gray-700">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${Math.min(100, Math.max(0, share))}%`, backgroundColor: color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const HealthRecordUsagePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeOtherIndex, setActiveOtherIndex] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PushNotificationCampaign | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState<PushNotificationCampaign | null>(null);
  const [usage, setUsage] = useState<{
    totalRecords: number;
    totalBytes: number;
    byType: HealthRecordUsageByType[];
    otherUserStorage?: HealthRecordUsageByType[];
    totalUsers: number;
    usersWithHealthRecordCount: number;
    usersWithoutHealthRecordCount: number;
    uploadSuccessRate?: number;
    avgUploadLatencyMs?: number;
    usersWithoutHealthRecordSample: {
      userId: string;
      name: string;
      phoneNumber: string;
      loginStatus: 'LOGGED_IN' | 'LOGGED_OUT';
    }[];
  } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchHealthRecordUsage();
      setUsage(res.data);
    } catch {
      toast.error('Failed to load health record usage.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const totalTypeBytes = useMemo(
    () => (usage?.byType || []).reduce((sum, row) => sum + Number(row.bytes || 0), 0),
    [usage]
  );

  const totalOtherBytes = useMemo(
    () => (usage?.otherUserStorage || []).reduce((sum, row) => sum + Number(row.bytes || 0), 0),
    [usage]
  );

  const chartData = useMemo(() => {
    if (!usage?.byType) return [];
    return usage.byType.map((row) => ({
      name: row.type || 'UNKNOWN',
      value: Number(row.bytes || 0),
      count: row.count,
    }));
  }, [usage]);

  const otherChartData = useMemo(() => {
    if (!usage?.otherUserStorage) return [];
    return usage.otherUserStorage.map((row) => ({
      name: formatFriendlyLabel(row.type || 'UNKNOWN'),
      value: Number(row.bytes || 0),
      count: row.count,
    }));
  }, [usage]);

  const handleNotifyClick = () => {
    setEditing({
      title: 'Complete your Health Record on Vedika',
      message: 'Your Health Record is still empty. Add your reports on Vedika to keep your health information ready.',
      notificationType: 'REMINDER',
      targetAudience: 'USERS_WITHOUT_HEALTH_RECORD',
      intentType: 'HEALTH_RECORDS',
      intentValue: 'MISSING_HEALTH_RECORD',
      targetPlatform: 'BOTH',
      status: 'DRAFT',
      createdAt: new Date().toISOString(),
    } as any);
    setFormOpen(true);
  };

  const handleFormReadyToSend = (campaign: PushNotificationCampaign) => {
    setSendTarget(campaign);
    setSendOpen(true);
  };

  return (
    <>
      <Toaster position="top-right" />
      <InsightsPageShell
        title="User Storage"
        description="Track health record storage, format breakdown, and dispatch missing-record reminders."
        info={pageInfo}
        actions={
          <button
            type="button"
            onClick={handleNotifyClick}
            disabled={loading || !usage}
            className={opsPrimaryBtn}
          >
            <BellRing className="h-4 w-4" />
            Notify Users Without Records
          </button>
        }
      >
        <div className={STAT_GRID_CLASS}>
          <InsightsStatCard
            label="Total Records"
            value={(usage?.totalRecords ?? 0).toLocaleString()}
            icon={FileText}
            accent="text-blue-600 dark:text-blue-400"
            loading={loading}
            info={healthRecordUsageMetricInfo.totalRecords}
          />
          <InsightsStatCard
            label="Storage Usage"
            value={formatBytes(usage?.totalBytes ?? 0)}
            icon={HardDrive}
            accent="text-purple-600 dark:text-purple-400"
            loading={loading}
            info={healthRecordUsageMetricInfo.storageUsage}
          />
          <InsightsStatCard
            label="Users With Records"
            value={(usage?.usersWithHealthRecordCount ?? 0).toLocaleString()}
            icon={Users}
            accent="text-emerald-600 dark:text-emerald-400"
            loading={loading}
            info={healthRecordUsageMetricInfo.usersWithRecords}
          />
          <InsightsStatCard
            label="Users Without Records"
            value={(usage?.usersWithoutHealthRecordCount ?? 0).toLocaleString()}
            icon={UserMinus}
            accent="text-amber-600 dark:text-amber-400"
            loading={loading}
            info={healthRecordUsageMetricInfo.usersWithoutRecords}
          />
          <InsightsStatCard
            label="Upload Success Rate"
            value={usage?.uploadSuccessRate != null ? `${usage.uploadSuccessRate.toFixed(1)}%` : '100%'}
            icon={Activity}
            accent="text-emerald-600 dark:text-emerald-400"
            loading={loading}
            info={healthRecordUsageMetricInfo.uploadSuccessRate}
          />
          <InsightsStatCard
            label="Avg Upload Latency"
            value={
              usage?.avgUploadLatencyMs != null && usage.avgUploadLatencyMs > 0
                ? `${(usage.avgUploadLatencyMs / 1000).toFixed(2)}s`
                : '—'
            }
            icon={Clock}
            accent="text-blue-600 dark:text-blue-400"
            loading={loading}
            info={healthRecordUsageMetricInfo.avgUploadLatency}
          />
        </div>

        <InsightsSectionHeader title="Health Records Tracking" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <InsightsChartCard
            title="Record Types Distribution"
            info={healthRecordUsageMetricInfo.recordTypesDistribution}
            className="lg:col-span-5"
            heightClass="h-[320px]"
          >
            <DonutChart
              data={chartData}
              total={totalTypeBytes}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
            />
          </InsightsChartCard>

          <div className={`${opsChartCard} lg:col-span-7`}>
            <InsightsSectionHeader
              title="Type Breakdown & Storage Weights"
              info={healthRecordUsageMetricInfo.typeBreakdownWeights}
              actions={
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {formatBytes(totalTypeBytes)} total
                </span>
              }
            />
            <BreakdownList
              rows={usage?.byType || []}
              total={totalTypeBytes}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
              emptyLabel="No health record uploads mapped in DB."
            />
          </div>
        </div>

        <InsightsSectionHeader title="Other Users Storage Tracking" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <InsightsChartCard
            title="Other User Storage Distribution"
            info={healthRecordUsageMetricInfo.otherUserStorageDistribution}
            className="lg:col-span-5"
            heightClass="h-[320px]"
          >
            <DonutChart
              data={otherChartData}
              total={totalOtherBytes}
              activeIndex={activeOtherIndex}
              setActiveIndex={setActiveOtherIndex}
              paletteOffset={3}
              labelFormatter={(name) => name}
            />
          </InsightsChartCard>

          <div className={`${opsChartCard} lg:col-span-7`}>
            <InsightsSectionHeader
              title="Other File Categories & Storage Weights"
              info={healthRecordUsageMetricInfo.otherUserTypeBreakdownWeights}
              actions={
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {formatBytes(totalOtherBytes)} total
                </span>
              }
            />
            <BreakdownList
              rows={usage?.otherUserStorage || []}
              total={totalOtherBytes}
              activeIndex={activeOtherIndex}
              setActiveIndex={setActiveOtherIndex}
              paletteOffset={3}
              labelFormatter={formatFriendlyLabel}
              emptyLabel="No other user uploads mapped in DB."
            />
          </div>
        </div>

        <div className={opsChartCard}>
          <InsightsSectionHeader
            title="Users Without Health Records (Sample Cohort)"
            actions={
              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600 dark:bg-blue-950/40 dark:text-blue-400">
                Showing {usage?.usersWithoutHealthRecordSample.length ?? 0} of {usage?.usersWithoutHealthRecordCount ?? 0}
              </span>
            }
          />
          <div className={opsTableWrap}>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800/80 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-2">User ID</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Phone Number</th>
                  <th className="px-4 py-2">Login Status</th>
                </tr>
              </thead>
              <tbody>
                {(usage?.usersWithoutHealthRecordSample || []).map((u) => (
                  <tr key={u.userId} className="border-t border-gray-100 dark:border-gray-700/60">
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-gray-500 dark:text-gray-400">
                      {u.userId}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">{u.name}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-gray-600 dark:text-gray-400">
                      {u.phoneNumber}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          u.loginStatus === 'LOGGED_IN'
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            u.loginStatus === 'LOGGED_IN' ? 'bg-emerald-500' : 'bg-gray-400'
                          }`}
                        />
                        {u.loginStatus === 'LOGGED_IN' ? 'Logged In' : 'Logged Out'}
                      </span>
                    </td>
                  </tr>
                ))}
                {!loading && (usage?.usersWithoutHealthRecordSample || []).length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                      All users have uploaded health records! Excellent compliance.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </InsightsPageShell>

      <CampaignFormModal
        open={formOpen}
        initial={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSaved={() => {
          toast.success('Saved.');
          void load();
        }}
        onReadyToSend={handleFormReadyToSend}
      />

      {sendOpen && sendTarget && (
        <SendConfirmModal
          key={sendTarget.id}
          open
          campaign={sendTarget}
          onClose={() => {
            setSendOpen(false);
            setSendTarget(null);
          }}
          onSent={() => {
            toast.success(sendTarget.status === 'SENT' ? 'Notification sent again.' : 'Notification sent.');
            void load();
          }}
        />
      )}
    </>
  );
};

export default HealthRecordUsagePage;
